import { type FC, type ReactNode, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Stack,
  Text,
  cn,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

export const ItemEditForm: FC<{
  title: string;
  saveLabel: string;
  saveDisabled?: boolean;
  disabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  deleteConfirmTitle?: string;
  deleteConfirmBody?: string;
  className?: string;
  children: ReactNode;
}> = ({
  title,
  saveLabel,
  saveDisabled,
  disabled,
  onCancel,
  onSave,
  onDelete,
  deleteLabel,
  deleteConfirmTitle,
  deleteConfirmBody,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div
      className={cn(
        'w-full rounded-lg border border-border-insights-subtle bg-bg-insights-subtle p-3.5',
        className,
      )}
    >
      <Text size="sm" weight="semibold">
        {title}
      </Text>
      {children}
      <Stack
        direction="horizontal"
        align="center"
        justify="between"
        gap={3}
        className="mt-4 border-t border-border-secondary pt-3"
      >
        {onDelete ? (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="border-border-negative-subtle text-text-negative hover:bg-bg-negative-subtle"
            onClick={() => setConfirmingDelete(true)}
          >
            {deleteLabel}
          </Button>
        ) : (
          <span />
        )}
        <Stack direction="horizontal" align="center" gap={2} fullWidth={false}>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            disabled={disabled || saveDisabled}
            onClick={onSave}
          >
            {saveLabel}
          </Button>
        </Stack>
      </Stack>
      {onDelete && (
        <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{deleteConfirmTitle}</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteConfirmBody}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={disabled}
                onClick={onDelete}
              >
                {t('carePlans.review.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};
